// 📄 pages/MiEspacio/sections/NotasSapSection.tsx
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, FileWarning, X, ExternalLink, Search, Tag, Users } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import {
  NotaSap, NotaSapData, NotaSapCompartida, MODULOS_NOTA_SAP, TIPOS_NOTA, SISTEMAS_AFECTADOS_NOTA
} from '../../../types/miEspacio.ts';
import { getNotasSap, saveNotaSap, deleteNotaSap } from '../../../services/miEspacioService.ts';
import { shareNotaSap, unshareNotaSap, getNotasSapCompartidas } from '../../../services/miEspacioCompartidoService.ts';
import { resolveNotaSapUrl } from '../../../services/knownErrorsService.ts';
import { formatFechaVerificacion } from '../../../utils/formatDate.ts';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import CopyIconButton from '../../../components/shared/CopyIconButton.tsx';
import { MiEspacioSeccionProps, etiquetaContexto } from '../miEspacioContexto.ts';

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-400 mb-1";

const TIPO_NOTA_STYLES: Record<string, string> = {
  'Error resuelto': 'bg-red-500/10 text-red-300 border-red-500/20',
  'Configuración': 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  'Proceso': 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  'Advertencia': 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  'Referencia general': 'bg-gray-500/10 text-gray-300 border-gray-500/20'
};

const emptyForm: NotaSapData = {
  titulo: '', numeroONota: '', modulo: MODULOS_NOTA_SAP[0], causa: '', solucion: '',
  tipoNota: TIPOS_NOTA[0], sistemaAfectado: '', campoTecnico: '', tags: [], versionAmbiente: '',
  fechaVerificacion: null, compartirConProyecto: false
};

const esUrl = (valor: string) => /^https?:\/\//i.test(valor.trim());
const esNumeroPuro = (valor: string) => /^\d+$/.test(valor.trim());

// A partir de lo que el usuario escribió en "Número o URL de la Nota SAP":
// - Ya es una URL completa (support.ariba.com, me.sap.com o cualquier otro
//   dominio) → se respeta tal cual, nunca se reescribe.
// - Es un número puro → se resuelve a me.sap.com vía resolveNotaSapUrl
//   (mismo criterio que Errores Conocidos e Incidentes).
// - Cualquier otra cosa (texto libre que no es número ni URL) → no hay
//   link resoluble, se devuelve null.
const resolverNotaSap = (valor: string): { url: string; badge: string; esUrl: boolean } | null => {
  const trimmed = valor.trim();
  if (!trimmed) return null;
  if (esUrl(trimmed)) return { url: trimmed, badge: 'Nota SAP', esUrl: true };
  if (esNumeroPuro(trimmed)) {
    return { url: resolveNotaSapUrl(trimmed)!, badge: `Nota SAP #${trimmed}`, esUrl: false };
  }
  return null;
};

// Firestore devuelve fechaVerificacion/fechaLimite como Timestamp (con
// `.toDate()`), no como Date — mismo criterio que KnownError.fechaVerificacion.
const toDate = (value: any): Date | null => value?.toDate?.() || (value instanceof Date ? value : null);
const toDateInputValue = (value: any): string => {
  const date = toDate(value);
  return date ? date.toISOString().slice(0, 10) : '';
};

// Vista unificada para renderizar tanto las notas propias (privadas, de
// usuarios/{uid}/...) como las compartidas por otros miembros del proyecto
// (de projects/{projectId}/notasSapCompartidas) en una sola lista.
interface NotaSapVista {
  id: string;
  projectId: string | null;
  titulo: string;
  numeroONota: string;
  modulo: string;
  causa: string;
  solucion: string;
  tipoNota?: string;
  sistemaAfectado?: string;
  campoTecnico?: string;
  tags?: string[];
  versionAmbiente?: string;
  fechaVerificacion?: any;
  compartida: boolean;
  puedeEditar: boolean;
  autorEmail?: string;
}

const matchesSearchNota = (n: NotaSapVista, term: string): boolean => {
  if (!term.trim()) return true;
  const needle = term.trim().toLowerCase();
  return [
    n.titulo, n.causa, n.solucion, n.numeroONota, n.tipoNota, n.sistemaAfectado,
    n.campoTecnico, n.versionAmbiente, ...(n.tags || [])
  ].filter(Boolean).some((campo) => (campo as string).toLowerCase().includes(needle));
};

export default function NotasSapSection({ contextosConsulta, contextoNuevo, proyectosPorId, mostrarProyecto }: MiEspacioSeccionProps) {
  const { user } = useAuth();
  const [notas, setNotas] = useState<NotaSap[]>([]);
  const [compartidas, setCompartidas] = useState<NotaSapCompartida[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [form, setForm] = useState<NotaSapData>(emptyForm);
  const [tagsInput, setTagsInput] = useState('');
  const [fechaVerificacionInput, setFechaVerificacionInput] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aEliminar, setAEliminar] = useState<NotaSapVista | null>(null);
  // Si la nota que se está editando ya tenía un espejo en
  // notasSapCompartidas antes de abrir el form — al guardar, solo tiene
  // sentido intentar borrarlo si de verdad existía (ver handleGuardar).
  // Para una nota nueva esto siempre es false: nunca hubo espejo que borrar.
  const [eraCompartidaAlAbrir, setEraCompartidaAlAbrir] = useState(false);

  const cargar = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [propias, compartidasDelProyecto] = await Promise.all([
        getNotasSap(user.uid, contextosConsulta),
        contextoNuevo ? getNotasSapCompartidas(contextoNuevo) : Promise.resolve([])
      ]);
      setNotas(propias);
      setCompartidas(compartidasDelProyecto);
    } catch (err) {
      console.error('Error cargando notas de SAP:', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [user?.uid, contextosConsulta.join(','), contextoNuevo]);

  const lista = useMemo<NotaSapVista[]>(() => {
    const propias: NotaSapVista[] = notas.map((n) => ({
      ...n, compartida: !!n.compartirConProyecto, puedeEditar: true
    }));
    const deOtros: NotaSapVista[] = compartidas
      .filter((c) => c.autorUid !== user?.uid)
      .map((c) => ({ ...c, projectId: contextoNuevo, compartida: true, puedeEditar: false }));
    const combinada = [...propias, ...deOtros];
    return combinada.filter((n) => matchesSearchNota(n, busqueda));
  }, [notas, compartidas, user?.uid, contextoNuevo, busqueda]);

  // Compartir/desvincular solo tiene sentido cuando la nota se guarda en un
  // proyecto puntual — tanto entrando por "Mi Espacio en el Proyecto" como
  // filtrando el Home a un único proyecto (no en Global ni en "Todos").
  const mostrarCompartir = (editingId ? editingProjectId : contextoNuevo) !== null;

  const openCreate = () => {
    setEditingId(null);
    setEditingProjectId(contextoNuevo);
    setForm(emptyForm);
    setTagsInput('');
    setFechaVerificacionInput('');
    setEraCompartidaAlAbrir(false);
    setError(null);
    setFormOpen(true);
  };

  const openEdit = (nota: NotaSapVista) => {
    if (!nota.puedeEditar) return;
    setEditingId(nota.id);
    setEditingProjectId(nota.projectId);
    setForm({
      titulo: nota.titulo, numeroONota: nota.numeroONota || '', modulo: nota.modulo,
      causa: nota.causa, solucion: nota.solucion,
      tipoNota: nota.tipoNota || TIPOS_NOTA[0], sistemaAfectado: nota.sistemaAfectado || '',
      campoTecnico: nota.campoTecnico || '', tags: nota.tags || [], versionAmbiente: nota.versionAmbiente || '',
      fechaVerificacion: null, compartirConProyecto: nota.compartida
    });
    setTagsInput((nota.tags || []).join(', '));
    setFechaVerificacionInput(toDateInputValue(nota.fechaVerificacion));
    setEraCompartidaAlAbrir(nota.compartida);
    setError(null);
    setFormOpen(true);
  };

  const handleGuardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setGuardando(true);
    setError(null);
    try {
      const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
      const targetProjectId = editingId ? editingProjectId : contextoNuevo;
      const payload: NotaSapData = {
        ...form,
        tags,
        fechaVerificacion: fechaVerificacionInput ? new Date(`${fechaVerificacionInput}T00:00:00`) : null,
        compartirConProyecto: targetProjectId ? !!form.compartirConProyecto : false
      };
      const id = await saveNotaSap(user.uid, editingId, payload, targetProjectId);
      if (targetProjectId) {
        if (payload.compartirConProyecto) {
          // create o update sobre notasSapCompartidas — en ambos casos el
          // autor coincide, la regla lo permite.
          await shareNotaSap(targetProjectId, id, payload, user.uid, user.email || '');
        } else if (eraCompartidaAlAbrir) {
          // Solo intentar borrar el espejo si de verdad existía: la regla
          // de `delete` exige `resource.data.autorUid`, que no existe si el
          // documento nunca se creó — intentarlo siempre (nota nueva o
          // nunca compartida) devuelve "Missing or insufficient permissions".
          await unshareNotaSap(targetProjectId, id).catch((err) => {
            console.error('Error al desvincular el espejo compartido:', err);
          });
        }
      }
      setFormOpen(false);
      cargar();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la nota.');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async () => {
    if (!user || !aEliminar || !aEliminar.puedeEditar) return;
    try {
      await deleteNotaSap(user.uid, aEliminar.id, aEliminar.projectId);
      if (aEliminar.projectId && aEliminar.compartida) {
        await unshareNotaSap(aEliminar.projectId, aEliminar.id).catch((err) => {
          console.error('Error al borrar el espejo compartido:', err);
        });
      }
      setAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar la nota:', err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap justify-between">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por título, causa, solución, sistema, tags..."
            className={`${inputClass} pl-9`}
          />
        </div>
        {!formOpen && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all flex-shrink-0">
            <Plus className="w-4 h-4" /> Nueva nota
          </button>
        )}
      </div>

      {formOpen && (
        <form onSubmit={handleGuardar} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <FileWarning className="w-4 h-4 text-blue-400" /> {editingId ? 'Editar nota' : 'Nueva nota'}
            </h2>
            <button type="button" onClick={() => setFormOpen(false)} className="text-gray-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          {/* Fila 1: Tipo de nota */}
          <div>
            <label className={labelClass}>Tipo de nota</label>
            <select className={inputClass} value={form.tipoNota || TIPOS_NOTA[0]} onChange={(e) => setForm({ ...form, tipoNota: e.target.value })}>
              {TIPOS_NOTA.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Fila 2: Título */}
          <div>
            <label className={labelClass}>Título / Nombre del Error</label>
            <input className={inputClass} value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ej: Migración Subcuentas a BTP" />
          </div>

          {/* Fila 3: Nota SAP | Sistema afectado */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Número o URL de la Nota SAP (opcional)</label>
              <div className="flex items-center gap-1.5">
                <input
                  className={inputClass}
                  value={form.numeroONota}
                  onChange={(e) => setForm({ ...form, numeroONota: e.target.value })}
                  placeholder="214285 o https://me.sap.com/notes/214285"
                />
                {(() => {
                  const preview = resolverNotaSap(form.numeroONota);
                  if (!preview) return null;
                  return (
                    <>
                      <CopyIconButton value={form.numeroONota} title="Copiar número/URL" />
                      <a
                        href={preview.url}
                        target="_blank"
                        rel="noreferrer"
                        title="Abrir nota"
                        className="flex-shrink-0 p-2.5 text-gray-400 hover:text-blue-400 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </>
                  );
                })()}
              </div>
            </div>
            <div>
              <label className={labelClass}>Sistema afectado</label>
              <select className={inputClass} value={form.sistemaAfectado || ''} onChange={(e) => setForm({ ...form, sistemaAfectado: e.target.value })}>
                <option value="">Sin especificar</option>
                {SISTEMAS_AFECTADOS_NOTA.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Fila 4: Módulo | Versión / Ambiente */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Módulo</label>
              <select className={inputClass} value={form.modulo} onChange={(e) => setForm({ ...form, modulo: e.target.value })}>
                {MODULOS_NOTA_SAP.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Versión / Ambiente verificado</label>
              <input className={inputClass} value={form.versionAmbiente || ''} onChange={(e) => setForm({ ...form, versionAmbiente: e.target.value })} placeholder="ej. Ariba 2402, ERP 6.0 EHP8, PRD" />
            </div>
          </div>

          {/* Fila 5: Campo técnico / Path XML */}
          <div>
            <label className={labelClass}>Campo técnico / Path XML</label>
            <input
              className={`${inputClass} font-mono`}
              value={form.campoTecnico || ''}
              onChange={(e) => setForm({ ...form, campoTecnico: e.target.value })}
              placeholder="ej. ResponsablePurchasingOrganizacionParty/InternalID"
            />
            <p className="text-[11px] text-gray-500 mt-1">El campo o ruta exacta donde ocurre el error o configuración.</p>
          </div>

          {/* Fila 6: Causa */}
          <div>
            <label className={labelClass}>Causa</label>
            <textarea className={`${inputClass} min-h-[80px]`} value={form.causa} onChange={(e) => setForm({ ...form, causa: e.target.value })} />
          </div>

          {/* Fila 7: Solución paso a paso */}
          <div>
            <label className={labelClass}>Solución (paso a paso)</label>
            <textarea className={`${inputClass} font-mono min-h-[120px]`} value={form.solucion} onChange={(e) => setForm({ ...form, solucion: e.target.value })} placeholder={"1. ...\n2. ...\n3. ..."} />
          </div>

          {/* Fila 8: Tags */}
          <div>
            <label className={labelClass}>Tags (separados por coma)</label>
            <input className={inputClass} value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="oauth, 401, token" />
          </div>

          {/* Fila 9: Fecha de verificación | Compartir con equipo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Fecha de verificación</label>
              <input type="date" className={inputClass} value={fechaVerificacionInput} onChange={(e) => setFechaVerificacionInput(e.target.value)} />
            </div>
            {mostrarCompartir && (
              <div>
                <label className={labelClass}>&nbsp;</label>
                <label className="flex items-start gap-2 text-xs text-gray-300 mt-2.5">
                  <input
                    type="checkbox"
                    checked={!!form.compartirConProyecto}
                    onChange={(e) => setForm({ ...form, compartirConProyecto: e.target.checked })}
                    className="accent-blue-500 w-4 h-4 mt-0.5"
                  />
                  <span>
                    Compartir con el equipo del proyecto
                    <span className="block text-[11px] text-gray-500">Los miembros del proyecto podrán ver esta nota en modo lectura.</span>
                  </span>
                </label>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={guardando} className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all">
              {guardando ? 'Guardando...' : 'Guardar nota'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Cargando notas...</p>
      ) : lista.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <FileWarning className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">{busqueda ? 'Ninguna nota coincide con la búsqueda.' : 'Aún no tienes notas de SAP guardadas.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {lista.map((nota) => {
            const notaLink = resolverNotaSap(nota.numeroONota || '');
            const verificacion = toDate(nota.fechaVerificacion) ? formatFechaVerificacion(toDate(nota.fechaVerificacion)!) : null;
            return (
              <div key={`${nota.projectId || 'global'}-${nota.id}`} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-white">{nota.titulo}</h3>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {nota.tipoNota && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${TIPO_NOTA_STYLES[nota.tipoNota] || TIPO_NOTA_STYLES['Referencia general']}`}>
                          {nota.tipoNota}
                        </span>
                      )}
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20 inline-block">{nota.modulo}</span>
                      {nota.sistemaAfectado && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-300 border border-sky-500/20">{nota.sistemaAfectado}</span>
                      )}
                      {nota.compartida && (
                        <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/20">
                          <Users className="w-3 h-3" /> Compartida
                        </span>
                      )}
                      {mostrarProyecto && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                          {etiquetaContexto(nota.projectId, proyectosPorId)}
                        </span>
                      )}
                    </div>
                  </div>
                  {nota.puedeEditar && (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button onClick={() => openEdit(nota)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => setAEliminar(nota)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {nota.campoTecnico && (
                  <p className="text-[11px] font-mono text-gray-400 truncate" title={nota.campoTecnico}>{nota.campoTecnico}</p>
                )}

                {notaLink && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">
                      {notaLink.badge}
                    </span>
                    <a href={notaLink.url} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
                      Abrir Nota <ExternalLink className="w-3 h-3" />
                    </a>
                    <CopyIconButton value={nota.numeroONota} title={notaLink.esUrl ? 'Copiar URL' : 'Copiar número'} className="text-gray-400 hover:text-white" />
                  </div>
                )}

                {nota.versionAmbiente && (
                  <p className="text-[11px] text-gray-500">Versión / Ambiente: <span className="text-gray-300">{nota.versionAmbiente}</span></p>
                )}

                {nota.causa && (
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Causa</p>
                    <p className="text-xs text-gray-300">{nota.causa}</p>
                  </div>
                )}
                {nota.solucion && (
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Solución</p>
                    <pre className="bg-black/30 rounded-lg p-3 text-xs text-gray-300 font-mono overflow-x-auto whitespace-pre-wrap">{nota.solucion}</pre>
                  </div>
                )}

                {nota.tags && nota.tags.length > 0 && (
                  <div className="flex items-center flex-wrap gap-1.5">
                    <Tag className="w-3 h-3 text-gray-500" />
                    {nota.tags.map((tag) => (
                      <span key={tag} className="text-[10px] bg-[rgba(91,155,240,0.1)] text-blue-400 border border-blue-400/20 px-2 py-0.5 rounded-full">{tag}</span>
                    ))}
                  </div>
                )}

                {verificacion && (
                  <p className={`text-[11px] ${verificacion.vencida ? 'text-amber-400' : 'text-gray-500'}`}>
                    Verificado el {verificacion.texto}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta nota?"
          message={`"${aEliminar.titulo}" se eliminará permanentemente.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
